import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { designTokens } from '@/utils/design-tokens'
import { DateRangePopoverProps } from './types'

const DateRangePopover: React.FC<DateRangePopoverProps> = ({
  isOpen,
  onClose,
  anchorElement,
  children,
  position = 'bottom-left'
}) => {
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        anchorElement &&
        !anchorElement.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose, anchorElement])

  const getPopoverStyle = (): React.CSSProperties => {
    if (!anchorElement) return { display: 'none' }

    const anchorRect = anchorElement.getBoundingClientRect()
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

    let top = 0
    let left = 0

    switch (position) {
      case 'bottom-left':
        top = anchorRect.bottom + scrollTop + 8
        left = anchorRect.left + scrollLeft
        break
      case 'bottom-right':
        top = anchorRect.bottom + scrollTop + 8
        left = anchorRect.right + scrollLeft - 420 // Updated calendar width with week column
        break
      case 'top-left':
        top = anchorRect.top + scrollTop - 8
        left = anchorRect.left + scrollLeft
        break
      case 'top-right':
        top = anchorRect.top + scrollTop - 8
        left = anchorRect.right + scrollLeft - 420
        break
    }

    // Ensure popover stays within viewport
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const maxPopoverWidth = Math.min(420, viewportWidth * 0.9) // Max 90% of viewport width
    const popoverHeight = 400
    const margin = 20 // Increased margin for safety

    // Ensure left position doesn't go negative or exceed viewport
    if (left < margin) {
      left = margin
    }
    if (left + maxPopoverWidth > viewportWidth - margin) {
      left = Math.max(margin, viewportWidth - maxPopoverWidth - margin)
    }

    // Ensure vertical position fits in viewport
    if (top + popoverHeight > viewportHeight + scrollTop - margin) {
      top = anchorRect.top + scrollTop - popoverHeight - 8 // Position above if no space below
    }

    return {
      position: 'absolute',
      top: `${top}px`,
      left: `${left}px`,
      zIndex: 1000,
      backgroundColor: designTokens.colors.white,
      border: `1px solid ${designTokens.colors.gray[2]}`,
      borderRadius: '8px',
      boxShadow: designTokens.boxShadow.medium,
      padding: 0,
      // 콘텐츠(일 달력 420px / 주 피커 320px / 월 피커 300px)가 자체 너비를 가지므로 컨테이너는 내용에 맞춤
      width: 'fit-content',
      maxWidth: '90vw'
    }
  }

  if (!isOpen) return null

  return createPortal(
    <div ref={popoverRef} style={getPopoverStyle()}>
      {children}
    </div>,
    document.body
  )
}

export default DateRangePopover